import { useMemo, useState } from "react";
import type { Lead } from "@/types/lead";
import { extractTranscriptCueLines, parseTranscriptLineToFollowUpDraft } from "@/lib/lead/transcriptFollowUpCue";
import { followUpFromLocalDateTimeInput, followUpToLocalDateTimeInput } from "@/lib/lead/followUpDateTimeLocal";
import { compactInputClass, ghostBtnClass, insetSurfaceCardClass, salesIndigoSurfaceCardClass } from "@/components/common/surfaceClasses";
import { TranscriptCueList } from "./TranscriptCueList";

type Props = {
  lead: Lead;
  onCreateFollowUp: (leadId: string, draft: { next_action: string; due_date: string; reason: string }) => void;
};

/** Remount with `key={lead.id}` so cue draft + dismissals reset when the selected lead changes. */
export function SalesConsoleCustomerCalendarCuePanel(props: Props) {
  const cues = useMemo(() => extractTranscriptCueLines(props.lead.transcript || ""), [props.lead.transcript]);
  const [activeCue, setActiveCue] = useState<string | null>(null);
  const [cueAction, setCueAction] = useState("");
  const [cueDueLocal, setCueDueLocal] = useState("");
  const [cueReason, setCueReason] = useState("");

  if (!cues.length) return null;

  function openCueEditor(line: string) {
    const d = parseTranscriptLineToFollowUpDraft(line);
    if (!d) return;
    setActiveCue(line);
    setCueAction(d.next_action);
    setCueDueLocal(followUpToLocalDateTimeInput(d.due_date));
    setCueReason(d.reason);
  }

  function cancelCueEditor() {
    setActiveCue(null);
    setCueAction("");
    setCueDueLocal("");
    setCueReason("");
  }

  function saveCueEditor() {
    if (!cueAction.trim() || !cueDueLocal) return;
    props.onCreateFollowUp(props.lead.id, {
      next_action: cueAction.trim(),
      due_date: followUpFromLocalDateTimeInput(cueDueLocal),
      reason: cueReason.trim() || "From transcript cue",
    });
    cancelCueEditor();
  }

  return (
    <>
      <div className={salesIndigoSurfaceCardClass}>
        <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">Transcript event cues</div>
        <p className="text-[10px] text-indigo-800/80 dark:text-indigo-300/80 mt-0.5">Expand a line to delete it or set up a follow-up.</p>
        <TranscriptCueList cues={cues} onUseForFollowUp={openCueEditor} />
      </div>

      {activeCue ? (
        <div className={`${insetSurfaceCardClass} border-indigo-200 space-y-2 dark:border-indigo-800/60`}>
          <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">Confirm event from cue</div>
          <p className="text-[10px] text-gray-600 dark:text-zinc-400 italic line-clamp-2">{activeCue}</p>
          <input
            className={compactInputClass}
            value={cueAction}
            onChange={(e) => setCueAction(e.target.value)}
            placeholder="Next action"
          />
          <input
            type="datetime-local"
            className={compactInputClass}
            value={cueDueLocal}
            onChange={(e) => setCueDueLocal(e.target.value)}
          />
          <textarea
            className={`${compactInputClass} min-h-[44px]`}
            value={cueReason}
            onChange={(e) => setCueReason(e.target.value)}
            placeholder="Reason / context"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!cueAction.trim() || !cueDueLocal}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              onClick={saveCueEditor}
            >
              Save as official event
            </button>
            <button type="button" className={`${ghostBtnClass} px-3 py-1.5 text-xs`} onClick={cancelCueEditor}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
